<template>
  <main class="chat-shell">
    <EmptyState
      v-if="!store.activeId"
      title="Start a requirement discussion"
      sub="Pick a conversation on the left, or start a new one. The AI PM will ask clarifying questions and draft a written-up requirement when the scope is clear."
    >
      <template #icon><Avatar kind="agent" :size="48" /></template>
    </EmptyState>

    <template v-else>
      <header class="chat-head">
        <div class="chat-head-main">
          <div class="chat-head-title" :title="store.detail?.title">
            {{ store.detail?.title ?? "—" }}
          </div>
          <div class="chat-head-meta">
            <StatusPill v-if="store.detail" :status="store.detail.status" size="sm" />
            <span class="last-updated" v-if="store.detail">
              · last updated {{ formatDateTime(store.detail.update_time) }}
            </span>
          </div>
        </div>
      </header>

      <div class="chat-body-wrap">
        <div class="chat-body" ref="bodyRef" @scroll="onBodyScroll">
          <template v-for="(item, idx) in renderItems" :key="idx">
            <MessageBubble v-if="item.kind === 'msg'" :msg="item.msg" />
          </template>

          <div v-if="showThinking" class="row-assistant">
            <Avatar kind="agent" :size="28" />
            <div class="typing">
              <span class="typing-label">Thinking</span>
              <span class="typing-dot" />
              <span class="typing-dot" />
              <span class="typing-dot" />
            </div>
          </div>
        </div>
        <button
          v-if="!stickToBottom && (streaming || renderItems.length > 0)"
          class="jump-latest"
          type="button"
          @click="jumpToLatest"
        >
          ↓ Jump to latest
        </button>
      </div>

      <div class="chat-input-wrap">
        <div class="chat-input">
          <div v-if="pendingItems.length" class="chat-attachments">
            <div
              v-for="item in pendingItems"
              :key="item.id"
              class="chat-attachment"
              :class="`is-${item.status}`"
            >
              <div
                v-if="item.status === 'uploading'"
                class="att-progress-fill"
                :style="{ width: item.progress + '%' }"
              />
              <img v-if="item.preview" :src="item.preview" class="att-thumb" alt="" />
              <div v-else class="att-thumb att-thumb-doc">
                {{ fileIconFor(item.file.type, item.file.name) }}
              </div>
              <div class="att-info">
                <div class="att-name" :title="item.file.name">{{ item.file.name }}</div>
                <div class="att-sub">
                  <span v-if="item.status === 'uploading'">
                    Uploading {{ Math.round(item.progress) }}%
                  </span>
                  <span v-else-if="item.status === 'done'" class="att-status-ok">
                    ✓ {{ formatBytes(item.file.size) }}
                  </span>
                  <span v-else-if="item.status === 'error'" class="att-status-err">
                    ⚠ {{ item.error || "Failed" }}
                  </span>
                </div>
              </div>
              <button
                v-if="item.status === 'error'"
                class="att-action"
                title="Retry"
                @click="retryItem(item.id)"
              >↻</button>
              <button
                class="att-action"
                :title="item.status === 'uploading' ? 'Cancel' : 'Remove'"
                @click="removeItem(item.id)"
              >×</button>
            </div>
          </div>

          <textarea
            v-model="draft"
            class="chat-textarea"
            placeholder="Describe a requirement, or follow up…"
            :disabled="streaming"
            rows="3"
            @keydown.meta.enter.prevent="onSend"
            @keydown.ctrl.enter.prevent="onSend"
            @paste="onPaste"
          />

          <div class="chat-input-actions">
            <div class="left-actions">
              <button class="ghost-btn" @click="filePicker?.click()">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="1.8"
                  stroke-linecap="round" stroke-linejoin="round">
                  <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 17.93 8.8l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
                Attach
              </button>
              <input
                ref="filePicker"
                type="file"
                :accept="ACCEPT_ATTR"
                multiple
                style="display: none"
                @change="onFileChosen"
              />
              <select
                v-model="model"
                class="model-select"
                @change="onModelChange"
              >
                <option v-for="m in modelOptions" :key="m" :value="m">{{ m }}</option>
              </select>
            </div>
            <button
              class="primary-btn"
              :disabled="sendDisabled"
              @click="onSend"
            >
              {{ sendLabel }}
              <span class="kbd">⌘ ↵</span>
            </button>
          </div>
        </div>
      </div>
    </template>
  </main>
</template>

<script setup lang="ts">
import { ref, reactive, markRaw, computed, nextTick, onMounted, watch } from "vue";
import { ElMessage } from "element-plus";
import MessageBubble from "./MessageBubble.vue";
import Avatar from "@/components/design/Avatar.vue";
import StatusPill from "@/components/design/StatusPill.vue";
import EmptyState from "@/components/design/EmptyState.vue";
import { useConversationStore } from "@/stores/conversation";
import { streamChat, type SSEEvent } from "@/api/sse";
import { uploadImage } from "@/api/uploads";
import { fetchConfig } from "@/api/config";
import { formatDateTime } from "@/lib/time";
import type { AttachmentRef, MessageOut } from "@/types/api";

const store = useConversationStore();
const draft = ref("");
const streaming = ref(false);
const bodyRef = ref<HTMLElement | null>(null);
const filePicker = ref<HTMLInputElement | null>(null);

const MAX_BINARY_BYTES = 10 * 1024 * 1024; // 10MB for images and PDFs
const MAX_TEXT_BYTES = 200 * 1024; // 200KB for text-based files (token-budget guard)
const MAX_FILES = 10;

const ACCEPT_EXTS = [
  ".png", ".jpg", ".jpeg", ".webp", ".gif",
  ".pdf",
  ".txt", ".md", ".log",
  ".csv", ".json", ".html", ".htm", ".xml",
];
const ACCEPT_MIMES = [
  "image/png", "image/jpeg", "image/webp", "image/gif",
  "application/pdf",
  "text/plain", "text/markdown", "text/csv",
  "application/json", "text/html", "text/xml", "application/xml",
];
const TEXT_EXTS = [".txt", ".md", ".log", ".csv", ".json", ".html", ".htm", ".xml"];
const ACCEPT_ATTR = [...ACCEPT_MIMES, ...ACCEPT_EXTS].join(",");

function isAcceptedFile(f: File): boolean {
  if (ACCEPT_MIMES.includes(f.type)) return true;
  // browsers sometimes leave .md/.log without a mime
  const lower = f.name.toLowerCase();
  return ACCEPT_EXTS.some((ext) => lower.endsWith(ext));
}

function isTextFile(f: File): boolean {
  const mime = (f.type || "").toLowerCase();
  if (mime.startsWith("text/")) return true;
  if (mime === "application/json" || mime === "application/xml") return true;
  const lower = f.name.toLowerCase();
  return TEXT_EXTS.some((ext) => lower.endsWith(ext));
}

function sizeCapFor(f: File): number {
  return isTextFile(f) ? MAX_TEXT_BYTES : MAX_BINARY_BYTES;
}

function fileIconFor(mime: string, name: string): string {
  const m = (mime || "").toLowerCase();
  if (m === "application/pdf" || name.toLowerCase().endsWith(".pdf")) return "PDF";
  if (m === "application/json" || name.toLowerCase().endsWith(".json")) return "{ }";
  if (m === "text/csv" || name.toLowerCase().endsWith(".csv")) return "CSV";
  if (m === "text/markdown" || name.toLowerCase().endsWith(".md")) return "MD";
  if (m === "text/html" || /\.html?$/.test(name.toLowerCase())) return "HTM";
  if (m.includes("xml") || name.toLowerCase().endsWith(".xml")) return "XML";
  return "TXT";
}

type PendingItem = {
  id: string;
  file: File;
  preview: string;
  status: "uploading" | "done" | "error";
  progress: number;
  attachment?: AttachmentRef;
  error?: string;
  abort: AbortController;
};

const pendingItems = ref<PendingItem[]>([]);

function genId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function formatBytes(n: number | null | undefined): string {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function startUpload(item: PendingItem) {
  if (!store.activeId) return;
  const convId = store.activeId;
  uploadImage(convId, item.file, {
    onProgress: (p) => {
      if (item.status === "uploading") item.progress = p;
    },
    signal: item.abort.signal,
  })
    .then((att) => {
      item.attachment = att;
      item.progress = 100;
      item.status = "done";
    })
    .catch((err: any) => {
      if (err?.name === "AbortError") return;
      item.status = "error";
      item.error = err?.message ?? "Upload failed";
    });
}

function addFiles(files: File[]) {
  if (!store.activeId) return;
  for (const file of files) {
    if (pendingItems.value.length >= MAX_FILES) {
      ElMessage.warning(`At most ${MAX_FILES} attachments per message`);
      break;
    }
    if (!isAcceptedFile(file)) {
      ElMessage.warning(`Skipped: ${file.name} — unsupported file type`);
      continue;
    }
    const cap = sizeCapFor(file);
    if (file.size > cap) {
      const kind = isTextFile(file) ? "Text files" : "Files";
      ElMessage.warning(
        `Skipped: ${file.name} exceeds ${formatBytes(cap)} (${kind} max ${formatBytes(cap)})`
      );
      continue;
    }
    const isImage = file.type.startsWith("image/");
    const item = reactive<PendingItem>({
      id: genId(),
      file: markRaw(file),
      preview: isImage ? URL.createObjectURL(file) : "",
      status: "uploading",
      progress: 0,
      abort: markRaw(new AbortController()),
    }) as PendingItem;
    pendingItems.value.push(item);
    startUpload(item);
  }
}

function safeRevoke(url: string) {
  if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
}

function removeItem(id: string) {
  const idx = pendingItems.value.findIndex((x) => x.id === id);
  if (idx < 0) return;
  const item = pendingItems.value[idx];
  if (item.status === "uploading") item.abort.abort();
  safeRevoke(item.preview);
  pendingItems.value.splice(idx, 1);
}

function retryItem(id: string) {
  const item = pendingItems.value.find((x) => x.id === id);
  if (!item) return;
  item.status = "uploading";
  item.progress = 0;
  item.error = undefined;
  item.abort = markRaw(new AbortController());
  startUpload(item);
}

function clearPending() {
  for (const it of pendingItems.value) {
    if (it.status === "uploading") it.abort.abort();
    safeRevoke(it.preview);
  }
  pendingItems.value = [];
}

const hasUploading = computed(() =>
  pendingItems.value.some((x) => x.status === "uploading")
);
const hasError = computed(() =>
  pendingItems.value.some((x) => x.status === "error")
);
const sendDisabled = computed(() => {
  if (streaming.value) return true;
  if (hasUploading.value || hasError.value) return true;
  return !draft.value.trim() && pendingItems.value.length === 0;
});
const sendLabel = computed(() => {
  if (streaming.value) return "Sending…";
  if (hasUploading.value) return "Uploading…";
  return "Send";
});

const MODEL_STORAGE_KEY = "ecosa.pm.model";
const modelOptions = ref<string[]>([]);
const model = ref<string>("");

function onModelChange() {
  if (model.value) localStorage.setItem(MODEL_STORAGE_KEY, model.value);
}

onMounted(async () => {
  const stored = localStorage.getItem(MODEL_STORAGE_KEY);
  try {
    const cfg = await fetchConfig();
    modelOptions.value = cfg.allowed_models;
    model.value =
      stored && cfg.allowed_models.includes(stored) ? stored : cfg.default_model;
    localStorage.setItem(MODEL_STORAGE_KEY, model.value);
  } catch {
    if (stored) model.value = stored;
  }
});

type MsgItem = { kind: "msg"; msg: MessageOut };

const renderItems = computed<MsgItem[]>(() =>
  store.messages
    .filter((m) => m.role !== "system" && m.role !== "tool")
    .filter((m) => m.role !== "assistant" || (m.content && m.content.trim().length > 0))
    .map((m) => ({ kind: "msg" as const, msg: m }))
);

// 流式中只要 token 一开始流，就让占位 "Thinking..." 消失，避免与气泡同屏并存。
const showThinking = computed(() => {
  if (!streaming.value) return false;
  const last = store.messages[store.messages.length - 1];
  return !(last && last.role === "assistant" && (last.content?.trim().length ?? 0) > 0);
});

// 「贴底」状态：true 时新内容自动滚到底；用户上翻后变 false，回到底部附近又恢复 true。
const stickToBottom = ref(true);
const BOTTOM_THRESHOLD_PX = 40;

function isNearBottom(el: HTMLElement): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight <= BOTTOM_THRESHOLD_PX;
}

function onBodyScroll() {
  const el = bodyRef.value;
  if (!el) return;
  stickToBottom.value = isNearBottom(el);
}

function jumpToLatest() {
  const el = bodyRef.value;
  if (!el) return;
  stickToBottom.value = true;
  el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
}

watch(
  () => store.activeId,
  async () => {
    draft.value = "";
    clearPending();
    stickToBottom.value = true;
    await nextTick();
    const el = bodyRef.value;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "auto" });
  }
);

const scrollSignal = computed(() => {
  const lastLen = store.messages[store.messages.length - 1]?.content?.length ?? 0;
  return `${store.messages.length}:${lastLen}:${streaming.value}`;
});

watch(scrollSignal, async () => {
  if (!stickToBottom.value) return;
  await nextTick();
  const el = bodyRef.value;
  if (!el) return;
  el.scrollTo({
    top: el.scrollHeight,
    behavior: streaming.value ? "auto" : "smooth",
  });
});

function onPaste(e: ClipboardEvent) {
  const items = e.clipboardData?.items;
  if (!items) return;
  const files: File[] = [];
  for (const it of items) {
    if (it.kind === "file" && it.type.startsWith("image/")) {
      const f = it.getAsFile();
      if (f) files.push(f);
    }
  }
  if (files.length) addFiles(files);
}

function onFileChosen(e: Event) {
  const input = e.target as HTMLInputElement;
  if (!input.files) return;
  addFiles(Array.from(input.files));
  input.value = "";
}

async function onSend() {
  if (!store.activeId) return;
  if (hasUploading.value) {
    ElMessage.info("Please wait for uploads to finish");
    return;
  }
  if (hasError.value) {
    ElMessage.warning("Some attachments failed — retry or remove them first");
    return;
  }
  const content = draft.value.trim();
  if (!content && pendingItems.value.length === 0) return;

  const attachments: AttachmentRef[] = pendingItems.value
    .filter((x) => x.status === "done" && x.attachment)
    .map((x) => x.attachment!) as AttachmentRef[];

  store.appendMessageLocally({
    id: -Date.now(),
    role: "user",
    content,
    attachments: attachments.length ? (attachments as any) : null,
    metadata: null,
    create_time: new Date().toISOString(),
  });
  store.appendMessageLocally({
    id: -Date.now() - 1,
    role: "assistant",
    content: "",
    attachments: null,
    metadata: null,
    create_time: new Date().toISOString(),
  });

  draft.value = "";
  clearPending();

  streaming.value = true;
  let errorMessage: string | null = null;
  try {
    for await (const ev of streamChat(store.activeId, {
      content,
      attachments,
      model: model.value,
    })) {
      const err = handleEvent(ev);
      if (err) errorMessage = err;
    }
  } catch (e: any) {
    errorMessage = e?.message ?? String(e);
  } finally {
    streaming.value = false;
    if (errorMessage) {
      reportStreamError(errorMessage);
      // Skip refresh so the user keeps seeing the error in-place.
      await store.loadList();
    } else {
      await Promise.all([store.refreshActive(), store.loadList()]);
    }
  }
}

/** Returns an error message string if this event represents a failure, else null. */
function handleEvent(ev: SSEEvent): string | null {
  if (ev.event === "token") {
    store.updateLastAssistant(ev.data.text);
  } else if (ev.event === "tool_call" || ev.event === "tool_result") {
    // Tool activity is intentionally hidden from business users; "Thinking…" is enough.
  } else if (ev.event === "error") {
    return ev.data.message || "Unknown server error";
  }
  return null;
}

function reportStreamError(message: string) {
  // 1) Persistent toast (must be dismissed manually).
  ElMessage({
    type: "error",
    message: `Chat failed: ${message}`,
    duration: 0,
    showClose: true,
  });
  // 2) Replace the empty assistant placeholder with a visible error bubble.
  store.setLastAssistant(`⚠️ **Error:** ${message}`);
  // eslint-disable-next-line no-console
  console.error("[chat stream]", message);
}
</script>

<style scoped>
.chat-shell {
  display: flex;
  flex-direction: column;
  background: var(--paper);
  overflow: hidden;
  min-width: 0;
  min-height: 0;
  height: 100%;
}
.chat-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 24px;
  background: var(--paper);
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}
.chat-head-main {
  flex: 1;
  min-width: 0;
}
.chat-head-title {
  font-size: 14.5px;
  font-weight: 600;
  color: var(--ink);
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  letter-spacing: -0.005em;
}
.chat-head-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  font-size: 11.5px;
  color: var(--ink-4);
}
.last-updated {
  color: var(--ink-4);
}
.chat-body-wrap {
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px 32px 12px;
  scroll-behavior: smooth;
  min-height: 0;
}
.jump-latest {
  position: absolute;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  padding: 6px 14px;
  font-size: 12.5px;
  line-height: 1.4;
  color: var(--ink-1, #1f2329);
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e3e5e8);
  border-radius: 999px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  z-index: 2;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}
.jump-latest:hover {
  background: var(--paper, #f6f7f9);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}
.row-assistant {
  display: flex;
  gap: 10px;
  margin: 14px 0;
  align-items: flex-start;
}
.typing {
  display: flex;
  gap: 6px;
  align-items: center;
  background: var(--surface);
  border: 1px solid var(--border);
  padding: 14px 18px;
  border-radius: 12px;
  border-top-left-radius: 4px;
}
.typing-label {
  font-size: 13px;
  color: var(--ink-3);
  margin-right: 2px;
}
.typing-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--ink-4);
  animation: bounce 1.2s infinite ease-in-out;
}
.typing-dot:nth-child(2) {
  animation-delay: 0.15s;
}
.typing-dot:nth-child(3) {
  animation-delay: 0.3s;
}
@keyframes bounce {
  0%,
  60%,
  100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  30% {
    transform: translateY(-4px);
    opacity: 1;
  }
}

.chat-input-wrap {
  padding: 8px 24px 18px;
  background: var(--paper);
  flex-shrink: 0;
}
.chat-input {
  background: var(--surface);
  border: 1px solid var(--border-2);
  border-radius: 12px;
  padding: 4px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.chat-input:focus-within {
  border-color: var(--ink-3);
  box-shadow: 0 0 0 4px rgba(11, 15, 20, 0.04);
}
.chat-textarea {
  width: 100%;
  border: none;
  outline: none;
  resize: none;
  background: transparent;
  padding: 12px 14px 4px;
  font-size: 14px;
  line-height: 1.55;
  color: var(--ink);
  min-height: 60px;
}
.chat-textarea::placeholder {
  color: var(--ink-4);
}
.chat-input-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px;
}
.left-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.chat-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 10px 4px;
}
.chat-attachment {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--hover);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 6px 8px 6px 6px;
  font-size: 12px;
  color: var(--ink-2);
  min-width: 200px;
  max-width: 280px;
  overflow: hidden;
}
.chat-attachment.is-error {
  border-color: #f3b8b1;
  background: #fff5f3;
}
.chat-attachment.is-done {
  border-color: #c4e3c9;
}
.att-progress-fill {
  position: absolute;
  inset: 0 auto 0 0;
  background: linear-gradient(
    90deg,
    rgba(50, 120, 220, 0.10),
    rgba(50, 120, 220, 0.18)
  );
  transition: width 0.15s ease;
  pointer-events: none;
  z-index: 0;
}
.att-thumb {
  position: relative;
  z-index: 1;
  width: 32px;
  height: 32px;
  object-fit: cover;
  border-radius: 4px;
  flex: 0 0 auto;
  background: #fff;
}
.att-thumb-doc {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: #475569;
  background: #eef2f7;
  border: 1px solid var(--border);
}
.att-info {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1 1 auto;
  line-height: 1.25;
}
.att-name {
  color: var(--ink);
  font-weight: 500;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.att-sub {
  font-size: 11px;
  color: var(--ink-4);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.att-status-ok {
  color: #2e8a3d;
}
.att-status-err {
  color: #c8392b;
}
.att-action {
  position: relative;
  z-index: 1;
  background: none;
  border: none;
  color: var(--ink-4);
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 2px 4px;
  border-radius: 4px;
  flex: 0 0 auto;
}
.att-action:hover {
  background: rgba(0, 0, 0, 0.06);
  color: var(--ink);
}
.ghost-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: var(--ink-3);
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}
.ghost-btn:hover {
  background: var(--hover);
  color: var(--ink);
}
.model-select {
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--ink-3);
  padding: 5px 8px;
  font-size: 12px;
  cursor: pointer;
}
.model-select:hover {
  border-color: var(--border-2);
  color: var(--ink);
}
.primary-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--ink);
  color: #fff;
  border: none;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s, opacity 0.15s;
}
.primary-btn:hover:not(:disabled) {
  background: #1a1f26;
}
.primary-btn:active:not(:disabled) {
  transform: scale(0.98);
}
.primary-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.kbd {
  background: rgba(255, 255, 255, 0.15);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10.5px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  margin-left: 4px;
}
</style>
